import { Thing, ThingType } from '@eagle/core-data-types';
import { FeatureTypes } from '@eagle/data-function-types';
import { Divider, Skeleton, Stack, Typography } from '@ethings/ui-toolkit';
import { Box, useTheme } from '@mui/material';
import { FC, ReactNode, useMemo, type JSX } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { evaluate } from '../../../evaluator';
import { useUiTemplate } from '../../../hooks';
import { EventDefinition } from '../../../notable-events';
import { Undefinable } from '../../../types';
import { CacheDataTypes } from '../../../types/cache';
import { DEFAULT_EVENT_TOOLTIP } from '../../../ui-templates';
import { getEventFeature, getEventFeatureV2, getEventLabel, getEventLabelV2, kphToMph } from '../../../util';
import { useNotableEventsContext } from '../../entity-journey';
import { getEventDictionary } from '../../entity-journey/util';
import { EventAddress } from '../../events/event-address';
import { FetchOne, FetchOneOfAll } from '../../fetch';
import { useBoolFlag } from '../../flags';
import { FormatTimestamp } from '../../format';
import { LabelledBadge } from '../../labelled-badge';
import { AnyEventData, isSummarizedEvent } from './thing-event-pane.types';

interface Props {
  data: AnyEventData[];
  dataLoaded?: () => void;
  requireLocation?: boolean;
}

const POPUP_LIMIT = 3;

export const EventBreadcrumbPopover: FC<Props> = ({ data: input, dataLoaded, requireLocation = true }) => {
  const { t } = useTranslation(['common', 'track']);
  const theme = useTheme();
  const { notableEvents } = useNotableEventsContext();
  const flagUseEventV3 = useBoolFlag('portals-translation-retrieval-hook-for-event-and-alert-descriptions-feature');
  const flagUseUITemplateTooltip = useBoolFlag('portals-ui-template-driven-event-tooltip-temporary');
  const { template, loaded: templateLoaded } = useUiTemplate('event-tooltip', DEFAULT_EVENT_TOOLTIP);
  const filteredInput = useMemo(() => requireLocation ? input.filter(({ data }) => !!data.location) : input, [input, requireLocation]);

  const eventStylesMemoized = useMemo(() => {
    const eventStyles = filteredInput.map((eventSnapshot) => getEventDictionary(eventSnapshot, theme, notableEvents));
    return eventStyles;
  }, [filteredInput, theme, notableEvents]);

  if (!filteredInput.length) return <></>;

  const renderContent = (item: Thing, thingType: ThingType, eventStyles: Undefinable<EventDefinition>, primary: string, secondary: string, eventSnapshot: AnyEventData): JSX.Element => {
    const { data, occurred } = eventSnapshot;
    const speed = data.velocity?.speed ?? 0;
    const speedText = t('common:component.events.labels.speed-value', { kph: speed, mph: kphToMph(speed) });
    const directionSx = { transform: `rotate(${data.velocity?.heading ?? 0}deg)` };
    const isSummarized = isSummarizedEvent(eventSnapshot);
    let summarizedEventsJsx = null;
    if (isSummarized && eventSnapshot._eventCount > 0) {
      summarizedEventsJsx = (
        <Trans i18nKey={`track:page.history.showing-${eventSnapshot._summarizationMode}-count-of-occurrences.labels`} values={{ count: eventSnapshot._eventCount }} components={[<span key={0} />]} />
      );
    }

    if (flagUseUITemplateTooltip) {
      if (!templateLoaded) {
        return <Skeleton animation="wave" width="376px" height="20px" variant="text" />;
      }
      const context = {
        item,
        thingType,
        eventStyles,
        primary,
        secondary,
        occurred,
        event: eventSnapshot,
        dataLoaded,
        speedText,
        data,
        speed,
        directionSx,
        summarizedEventsJsx,
        theme,
      };

      try {
        const content = evaluate(template, context) as ReactNode;
        return <>{content ?? null}</>;
      } catch (error) {
        console.error('Template evaluation failed', error);
      }
    }

    return <>
      <Stack direction="row" justifyContent="space-between" mb={1}>
        <Box>
          <Typography fontWeight="bold">{item.display}</Typography>
          <Typography variant="body1">{thingType.display}</Typography>
        </Box>
        <Stack direction="row" alignItems="center">
          <LabelledBadge
            align="left"
            background={eventStyles?.styling.backgroundColor}
            color={eventStyles?.styling.textColor}
            label={primary}
            subtitle={secondary}
          />
        </Stack>
      </Stack>
      {!!data.location && (
        <Stack direction="row" spacing={1}>
          <Typography fontWeight="bold">{t('common:terms.location')}</Typography>
          <EventAddress
            addressUnavailableText={t('common:component.map.hint.no-location')}
            dataLoaded={dataLoaded}
            loadingComponent={<Skeleton height={'20px'} width={'100%'} />}
            location={data.location}
            variant="body1"
            typographyProps={{
              whiteSpace: 'wrap',
            }}
          />
        </Stack>
      )}
      {occurred && (
        <Stack direction="row" spacing={1}>
          <Typography fontWeight="bold">{t('common:component.map.labels.occurred-at')}</Typography>
          <Typography>
            <FormatTimestamp value={occurred} />
          </Typography>
        </Stack>
      )}
      {!isNaN(speed) && (
        <Stack direction="row" spacing={1}>
          <Typography fontWeight="bold">{t('common:component.events.labels.speed')}</Typography>
          <Typography>{t('common:component.events.labels.speed-value', { kph: speed, mph: kphToMph(speed) })}</Typography>
        </Stack>
      )}
    </>;
  };

  return (
    <>
      {filteredInput.slice(0, POPUP_LIMIT + 1).map((eventSnapshot, i) => {
        const { eventTypeId, feature, featureTypeId, thingId } = eventSnapshot;
        const eventStyles = eventStylesMemoized[i];

        if (i === POPUP_LIMIT) {
          return (
            <Box key={i} sx={{ pt: 2 }}>
              <Typography>{t('common:component.map.labels.more-items', { count: filteredInput.length - i })}</Typography>
            </Box>
          );
        }
        const featureLabel = flagUseEventV3 ? getEventFeature(feature) : getEventFeatureV2(feature);
        const eventTypeLabel = flagUseEventV3 ? getEventLabel(feature, eventTypeId) : getEventLabelV2(featureTypeId, eventTypeId);
        const primary = featureTypeId === FeatureTypes.EVENT_RECORD_V0 ? featureLabel : eventTypeLabel;
        const secondary = featureTypeId === FeatureTypes.EVENT_RECORD_V0 ? eventTypeLabel : featureLabel;

        return (
          <Stack key={i} maxWidth="100vw">
            {i !== 0 && <Divider />}
            <FetchOne
              id={thingId}
              dataType={CacheDataTypes.THING}
              loadingFactory={() => <Skeleton variant="circular" height={62} />}
              renderFactory={(item: Thing) => (
                <FetchOneOfAll
                  id={item.thingTypeId}
                  dataType={CacheDataTypes.THING_TYPE}
                  renderFactory={(thingType: ThingType) => renderContent(item, thingType, eventStyles, primary, secondary, eventSnapshot)}
                />
              )}
            />
          </Stack>
        );
      })}
    </>
  );
};
